import React, { useState } from "react";
import { PAGE_SIZE, SC, ST, byUrgency, countByStatus, fmt, late, stg } from "./npdUtils";

// Stacked status bar: in progress / completed / review / delayed
export const Bar = ({ counts }) => {
  const total = counts.reduce((a, b) => a + b, 0) || 1;
  return (
    <div className="bar">
      {[0, 1, 2, 3].map((i) => (
        <i key={i} className={`c-${SC[i]}`} style={{ width: `${(counts[i] / total) * 100}%` }} />
      ))}
    </div>
  );
};

export const Kpi = ({ n, label, color }) => (
  <div className="k" style={color ? { borderTopColor: `var(--${color})` } : undefined}>
    <b>{n}</b>
    <span>{label}</span>
  </div>
);

export const LineTable = ({ lines, parts, onOpen, onEdit }) => {
  if (!lines.length) {
    return <div className="empty">No assembly line matches. Clear the search or status filter.</div>;
  }
  return (
    <table>
      <thead>
        <tr>
          <th>Line</th><th>Product</th><th>Parts</th><th>In progress</th><th>Completed</th>
          <th>Review</th><th>Delayed</th><th style={{ width: 200 }}>Status</th>{onEdit && <th />}
        </tr>
      </thead>
      <tbody>
        {lines.map((l) => {
          const lp = parts.filter((p) => p.L.id === l.id);
          const c = countByStatus(lp);
          return (
            <tr key={l.id} className="r" onClick={() => onOpen(l)}>
              <td>{l.name}</td><td>{l.product}</td><td>{lp.length}</td><td>{c[0]}</td><td>{c[1]}</td>
              <td>{c[2]}</td><td className={c[3] ? "red" : ""}>{c[3]}</td><td><Bar counts={c} /></td>
              {onEdit && (
                <td>
                  <button className="btn s" onClick={(e) => { e.stopPropagation(); onEdit(l); }}>Edit</button>
                </td>
              )}
            </tr>
          );
        })}
      </tbody>
    </table>
  );
};

// Searchable, filterable, paginated parts table (used by the tracker, parts page and buyer dashboard)
export const PartsTable = ({
  list, showLine, today, isAdmin, me, buyers = [], lines = [],
  pre, engFilter, lineFilter, mineFilter, defaultMine = false, onOpen, onAdvance,
}) => {
  const [pq, setPq] = useState("");
  const [ps, setPs] = useState("");
  const [pe, setPe] = useState("");
  const [pl, setPl] = useState("");
  const [mine, setMine] = useState(defaultMine);
  const [page, setPage] = useState(0);
  const isBuyer = !isAdmin;
  const k = pq.toLowerCase();

  const f = list
    .filter(
      (p) =>
        (!k || (p.name + p.partNo + (p.supplierName || "")).toLowerCase().includes(k)) &&
        (!ps || p.s === ps - 1) &&
        (!pe || p.buyerId === pe) &&
        (!mine || p.buyerId === me?.orgUserId) &&
        (!pl || String(p.L.id) === pl)
    )
    .sort(byUrgency(today));
  const pg = Math.max(0, Math.min(page, Math.ceil(f.length / PAGE_SIZE) - 1));
  const reset = (setter) => (e) => { setter(e.target.value); setPage(0); };

  return (
    <>
      <div className="tools one-line">
        {pre}
        <input placeholder="Search part, ID or supplier" value={pq} onChange={reset(setPq)} />
        <select value={ps} onChange={reset(setPs)}>
          <option value="">All statuses</option>
          {ST.map((s, i) => <option key={s} value={i + 1}>{s}</option>)}
        </select>
        {engFilter && (
          <select value={pe} onChange={reset(setPe)}>
            <option value="">All buyers</option>
            {buyers.map((b) => <option key={b.orgUserId} value={b.orgUserId}>{b.name}</option>)}
          </select>
        )}
        {lineFilter && (
          <select value={pl} onChange={reset(setPl)}>
            <option value="">All lines</option>
            {lines.map((l) => <option key={l.id} value={String(l.id)}>{l.name}</option>)}
          </select>
        )}
        {/* {mineFilter && (
          <label className="chk">
            <input type="checkbox" checked={mine} onChange={(e) => { setMine(e.target.checked); setPage(0); }} /> My parts only
          </label>
        )} */}
      </div>
      <div className="wrap">
        {!f.length ? (
          <div className="empty">No parts match. Clear the search, status or "My parts only" filter.</div>
        ) : (
          <>
            <table>
              <thead>
                <tr>
                  <th>Part</th>{showLine && <th>Line</th>}<th>Current stage</th><th>Progress</th><th>Status</th>
                  <th>Purchasing engineer</th><th>Target date</th><th>Delay</th>{isBuyer && <th />}
                </tr>
              </thead>
              <tbody>
                {f.slice(pg * PAGE_SIZE, pg * PAGE_SIZE + PAGE_SIZE).map((p) => {
                  const N = p.L.stages.length;
                  const d = late(p, today);
                  return (
                    <tr key={p.id} className="r" onClick={() => onOpen(p.id)}>
                      <td>{p.partNo}<br /><span className="mut">{p.name}</span></td>
                      {showLine && <td>{p.L.name}</td>}
                      <td>{stg(p)}<br /><span className="mut">Stage {Math.min(p.cur + 1, N)} of {N}</span></td>
                      <td>
                        <div className="seg">
                          {p.L.stages.map((_, i) => (
                            <i key={i} className={i < p.cur ? "d" : i === p.cur ? `cur ${SC[p.s]}` : ""} />
                          ))}
                        </div>
                      </td>
                      <td><span className={`tag c-${SC[p.s]}`}>{ST[p.s]}</span></td>
                      <td>{p.buyerName}</td>
                      <td>{fmt(p.target)}</td>
                      <td>
                        {p.s === 1 ? "Done" : d ? <span className="red">{d} days late</span>
                          : p.s === 3 ? <span className="red">Flagged delayed</span> : "On time"}
                      </td>
                      {isBuyer && (
                        <td>
                          {p.buyerId === me?.orgUserId && p.s !== 1 && (
                            <button className="btn" onClick={(e) => { e.stopPropagation(); onAdvance(p.id); }}>
                              Complete stage
                            </button>
                          )}
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
            <div className="tools" style={{ margin: "12px 0 0", alignItems: "center" }}>
              <button className="btn" onClick={() => setPage(pg - 1)} disabled={!pg}>Previous</button>
              <span>{pg * PAGE_SIZE + 1}–{Math.min(f.length, pg * PAGE_SIZE + PAGE_SIZE)} of {f.length} parts</span>
              <button className="btn" onClick={() => setPage(pg + 1)} disabled={(pg + 1) * PAGE_SIZE >= f.length}>Next</button>
            </div>
          </>
        )}
      </div>
    </>
  );
};
